import type { Intent } from '../types/badges'
import type { SelectFilterOption } from '../types/filter'

export type DeliveryReasonClass = 'inherited' | 'actionable' | 'informational'

export type DeliveryReasonMetadata = {
    key: string
    label: string
    explanation: string
    class: DeliveryReasonClass
    priority: number
    intent: Intent
}

export type DeliveryStatusMetadata = {
    key: string
    label: string
    explanation: string
    intent?: Intent
}

const inherited = (key: string, label: string, explanation: string): DeliveryReasonMetadata => ({
    key,
    label,
    explanation,
    class: 'inherited',
    priority: 100,
    intent: 'primary',
})
const actionable = (
    key: string,
    label: string,
    explanation: string,
    priority: number,
    intent: Intent = 'warning',
): DeliveryReasonMetadata => ({
    key,
    label,
    explanation,
    class: 'actionable',
    priority,
    intent,
})
const informational = (
    key: string,
    label: string,
    explanation: string,
): DeliveryReasonMetadata => ({
    key,
    label,
    explanation,
    class: 'informational',
    priority: 100,
    intent: 'primary',
})

// Keep this in the canonical serialisation order of DeliveryReason in
// rusty-ingestion/crates/advertising-api/src/exports/export_campaigns.rs.
export const deliveryReasonMetadata: readonly DeliveryReasonMetadata[] = [
    inherited(
        'CAMPAIGN_PAUSED',
        'Campaign paused',
        'This item is not delivering because its campaign is paused.',
    ),
    inherited(
        'CAMPAIGN_ARCHIVED',
        'Campaign archived',
        'This item is not delivering because its campaign is archived.',
    ),
    inherited(
        'CAMPAIGN_PENDING_START_DATE',
        'Campaign has not started',
        'This campaign is scheduled to start in the future.',
    ),
    inherited(
        'CAMPAIGN_END_DATE_REACHED',
        'Campaign ended',
        'This campaign has reached its end date.',
    ),
    actionable(
        'CAMPAIGN_REJECTED',
        'Campaign rejected',
        'Amazon rejected this campaign. Review the campaign in Amazon Ads for details.',
        10,
        'danger',
    ),
    actionable(
        'CAMPAIGN_PENDING_REVIEW',
        'Campaign pending review',
        'Amazon is reviewing this campaign.',
        30,
    ),
    actionable(
        'CAMPAIGN_INCOMPLETE',
        'Campaign incomplete',
        'This campaign needs more information before it can deliver.',
        20,
    ),
    actionable(
        'CAMPAIGN_OUT_OF_BUDGET',
        'Campaign out of budget',
        'This campaign has used its available budget.',
        4,
        'danger',
    ),
    inherited(
        'PORTFOLIO_OUT_OF_BUDGET',
        'Portfolio out of budget',
        'This item is not delivering because its portfolio has used its budget.',
    ),
    inherited(
        'PORTFOLIO_END_DATE_REACHED',
        'Portfolio ended',
        'This item is not delivering because its portfolio has reached its end date.',
    ),
    inherited(
        'PORTFOLIO_ARCHIVED',
        'Portfolio archived',
        'This item is not delivering because its portfolio is archived.',
    ),
    inherited(
        'PORTFOLIO_PAUSED',
        'Portfolio paused',
        'This item is not delivering because its portfolio is paused.',
    ),
    inherited(
        'PORTFOLIO_PENDING_START_DATE',
        'Portfolio has not started',
        'This item is not delivering because its portfolio has not started yet.',
    ),
    actionable(
        'ADVERTISER_POLICING_SUSPENDED',
        'Account suspended by policy',
        'Amazon has suspended this advertising account for a policy reason.',
        2,
        'danger',
    ),
    actionable(
        'ADVERTISER_POLICING_PENDING_REVIEW',
        'Account pending policy review',
        'Amazon is reviewing this advertising account for a policy reason.',
        15,
    ),
    inherited('ADVERTISER_ARCHIVED', 'Account archived', 'This advertising account is archived.'),
    actionable(
        'ADVERTISER_OUT_OF_BUDGET',
        'Account out of budget',
        'This advertising account has used its available budget.',
        2,
        'danger',
    ),
    inherited('ADVERTISER_PAUSED', 'Account paused', 'This advertising account is paused.'),
    actionable(
        'ADVERTISER_PAYMENT_FAILURE',
        'Payment problem',
        'Amazon could not process payment for this advertising account. Update the account payment method in Amazon Ads.',
        1,
        'danger',
    ),
    actionable(
        'ADVERTISER_OUT_OF_PREPAY_BALANCE',
        'Prepay balance empty',
        'This advertising account has no prepay balance remaining.',
        1,
        'danger',
    ),
    actionable(
        'ADVERTISER_INELIGIBLE',
        'Account ineligible',
        'This advertising account is not eligible to deliver.',
        5,
        'danger',
    ),
    inherited(
        'AD_GROUP_PAUSED',
        'Ad group paused',
        'This item is not delivering because its ad group is paused.',
    ),
    inherited(
        'AD_GROUP_ARCHIVED',
        'Ad group archived',
        'This item is not delivering because its ad group is archived.',
    ),
    actionable(
        'AD_GROUP_INCOMPLETE',
        'Ad group incomplete',
        'This ad group needs more information before it can deliver.',
        20,
    ),
    actionable(
        'AD_GROUP_POLICING_PENDING_REVIEW',
        'Ad group pending policy review',
        'Amazon is reviewing this ad group for a policy reason.',
        15,
    ),
    actionable(
        'AD_GROUP_LOW_BID',
        'Bid too low',
        'Raise the bid to make this ad group eligible for more auctions.',
        25,
    ),
    actionable(
        'AD_GROUP_PENDING_REVIEW',
        'Ad group pending review',
        'Amazon is reviewing this ad group.',
        30,
    ),
    actionable(
        'AD_GROUP_REJECTED',
        'Ad group rejected',
        'Amazon rejected this ad group. Review it in Amazon Ads for details.',
        10,
        'danger',
    ),
    actionable(
        'TARGET_POLICING_SUSPENDED',
        'Target suspended by policy',
        'Amazon suspended this target for a policy reason.',
        10,
        'danger',
    ),
    actionable('TARGET_PAUSED', 'Target paused', 'This target is paused.', 20),
    actionable('TARGET_ARCHIVED', 'Target archived', 'This target is archived.', 20),
    actionable(
        'TARGET_BLOCKED',
        'Target blocked',
        'Amazon has blocked this target from delivering.',
        10,
        'danger',
    ),
    actionable('AD_PAUSED', 'Product ad paused', 'This product ad is paused.', 20),
    actionable('AD_ARCHIVED', 'Product ad archived', 'This product ad is archived.', 20),
    actionable(
        'AD_POLICING_PENDING_REVIEW',
        'Product ad pending policy review',
        'Amazon is reviewing this product ad for a policy reason.',
        15,
    ),
    actionable(
        'AD_POLICING_SUSPENDED',
        'Product ad suspended by policy',
        'Amazon suspended this product ad for a policy reason.',
        10,
        'danger',
    ),
    actionable(
        'AD_CREATION_FAILED',
        'Product ad creation failed',
        'Amazon could not create this product ad.',
        10,
        'danger',
    ),
    actionable(
        'AD_CREATION_IN_PROGRESS',
        'Product ad being created',
        'Amazon is still creating this product ad.',
        30,
    ),
    actionable(
        'LANDING_PAGE_NOT_AVAILABLE',
        'Product page unavailable',
        'The product detail page is not currently available.',
        15,
    ),
    actionable(
        'NOT_BUYABLE',
        'Product not buyable',
        'The advertised product is not currently buyable.',
        15,
    ),
    actionable(
        'NOT_IN_BUYBOX',
        'Not in the Buy Box',
        'The advertised product is not currently eligible for the Buy Box.',
        20,
    ),
    actionable(
        'NOT_IN_POLICY',
        'Not eligible under policy',
        'This product ad is not eligible under Amazon policy.',
        10,
        'danger',
    ),
    actionable('OUT_OF_STOCK', 'Out of stock', 'The advertised product is out of stock.', 15),
    actionable(
        'AD_MISSING_IMAGE',
        'Product image missing',
        'The advertised product needs an image.',
        20,
    ),
    actionable(
        'SECURITY_SCAN_PENDING_REVIEW',
        'Security review pending',
        'Amazon is reviewing this product for security concerns.',
        15,
    ),
    actionable(
        'SECURITY_SCAN_REJECTED',
        'Security review rejected',
        'Amazon rejected this product in a security review.',
        10,
        'danger',
    ),
    informational(
        'STATUS_UNAVAILABLE',
        'Status unavailable',
        'Amazon could not provide a delivery status for this item.',
    ),
    actionable(
        'NO_INVENTORY',
        'No inventory',
        'The advertised product has no available inventory.',
        15,
    ),
    actionable(
        'NO_PURCHASABLE_OFFER',
        'No purchasable offer',
        'The advertised product has no purchasable offer.',
        15,
    ),
    actionable(
        'AD_INELIGIBLE',
        'Product ad ineligible',
        'This product ad is not eligible to deliver.',
        10,
        'danger',
    ),
    actionable(
        'CREATIVE_MISSING_ASSET',
        'Creative asset missing',
        'This creative is missing an asset required to deliver.',
        20,
    ),
    actionable(
        'CREATIVE_PENDING_REVIEW',
        'Creative pending review',
        'Amazon is reviewing this creative.',
        30,
    ),
    actionable(
        'CREATIVE_REJECTED',
        'Creative rejected',
        'Amazon rejected this creative. Review it in Amazon Ads for details.',
        10,
        'danger',
    ),
    actionable(
        'AD_MISSING_DECORATION',
        'Product ad information missing',
        'This product ad is missing information Amazon requires.',
        20,
    ),
    actionable(
        'PIR_RULE_EXCLUDED',
        'Excluded by an Amazon rule',
        'Amazon excluded this product ad under an eligibility rule.',
        15,
    ),
    actionable(
        'AD_NOT_DELIVERING',
        'Campaign ads not delivering',
        'Amazon reports that the ads in this campaign are not delivering.',
        20,
    ),
    informational('OTHER', 'Other delivery reason', 'Amazon reported another delivery reason.'),
    informational(
        'INELIGIBLE',
        'Ineligible',
        'Amazon reports that this item is ineligible to deliver.',
    ),
    informational(
        'TARGETING_CLAUSE_STATUS_LIVE',
        'Target status live',
        'Amazon reported this target as live.',
    ),
    informational(
        'ADVERTISER_EXCEEDS_SPENDING_LIMIT',
        'Account spending limit reached',
        'Amazon reports that this account has reached its spending limit.',
    ),
    informational(
        'AD_STATUS_LIVE',
        'Product ad status live',
        'Amazon reported this product ad as live.',
    ),
    informational('REJECTED', 'Rejected', 'Amazon reported this item as rejected.'),
    informational(
        'PENDING_REVIEW',
        'Pending review',
        'Amazon reported this item as pending review.',
    ),
]

export const deliveryReasonMetaByKey: Readonly<Record<string, DeliveryReasonMetadata>> =
    Object.fromEntries(deliveryReasonMetadata.map((metadata) => [metadata.key, metadata]))

export const deliveryReasonOrderByKey: Readonly<Record<string, number>> = Object.fromEntries(
    deliveryReasonMetadata.map((metadata, index) => [metadata.key, index]),
)

export const deliveryStatusMetadata: readonly DeliveryStatusMetadata[] = [
    {
        key: 'DELIVERING',
        label: 'Delivering',
        explanation: 'Amazon reports this item is delivering.',
        intent: 'success',
    },
    {
        key: 'NOT_DELIVERING',
        label: 'Not delivering',
        explanation: 'Amazon reports this item is not delivering.',
        intent: 'danger',
    },
    {
        key: 'UNAVAILABLE',
        label: 'Unavailable',
        explanation: 'Amazon could not provide the current delivery status.',
        intent: 'warning',
    },
    { key: 'OTHER', label: 'Other', explanation: 'Amazon reported another delivery status.' },
]

export const deliveryStatusMetaByKey: Readonly<Record<string, DeliveryStatusMetadata>> =
    Object.fromEntries(deliveryStatusMetadata.map((metadata) => [metadata.key, metadata]))

export const deliveryStatusBadgeOptions = Object.fromEntries(
    deliveryStatusMetadata.map((metadata) => [
        metadata.key,
        { text: metadata.label, intent: metadata.intent },
    ]),
)

export const deliveryReasonFilterOptions: SelectFilterOption[] = deliveryReasonMetadata.map(
    ({ key, label }) => ({ value: key, name: label }),
)

export const deliveryStatusFilterOptions: SelectFilterOption[] = deliveryStatusMetadata.map(
    ({ key, label }) => ({ value: key, name: label }),
)

export const unknownDeliveryReason = (key: string): DeliveryReasonMetadata => ({
    key,
    label: key,
    explanation: `Amazon reported an unrecognised delivery reason: ${key}.`,
    class: 'informational',
    priority: Number.MAX_SAFE_INTEGER,
    intent: 'primary',
})

export const getDeliveryReasonMetadata = (key: string): DeliveryReasonMetadata =>
    deliveryReasonMetaByKey[key] ?? unknownDeliveryReason(key)
